// insuranceDiscovery.js — INSURE-2 (P12): the one owner of "is there a second plan?" and of
// what a discovered secondary is, for Home (the 90-day recheck and the COB sheet) and onboarding.
//
// ★★ WHY. The 90-day recheck returned false whenever the applied plan was still active, so a
//   second active plan that appeared later was never offered (INSURE-2a). And "Add as 2nd" on a
//   discovered plan with no member ID posted memberId '': the server reads that as a CLEAR,
//   deleted the patient's existing secondary and answered { secondary: null }, and the app
//   then stored its own local object and showed success.
//
// Rules (plan P12 step 5, Alex D5 / D6):
//   - a second plan counts only when active === true, it has a non-empty normalised
//     memberIdGuess (D5), and it is neither the primary's member id nor the secondary's on file;
//   - an ended primary still counts as a change, and so does a vanished primary plus a new
//     active plan (unchanged from v242.7.0);
//   - "No, just the one plan" saves nothing, so the offer comes back at the next recheck (D6);
//     only a saved secondary stops it;
//   - a secondary is saved only when the server echoes an object whose memberId is the one sent.
//
// No imports on purpose: gates import this file directly under plain node.

export function normMemberId(s) {
  return String(s || '').replace(/\s/g, '').toLowerCase()
}

// PROD-1133: do two secondary policies name the SAME coverage? The same fields as the API's
//   coverage key (rcm_service._coverage_key_message, secondary side): payer id, carrier name,
//   member ID, relationship, and the policyholder's name and date of birth when the relationship
//   is not Self. Used only to decide whether the on-screen result still belongs to the saved
//   plan; the values are compared in memory and never logged.
export function sameSecondaryCoverage(a, b) {
  if (!a || typeof a !== 'object' || !b || typeof b !== 'object') return false
  const norm = (v) => String(v ?? '').replace(/\s+/g, ' ').trim().toLowerCase()
  const rel = (x) => norm(x.subscriberRelationship) || 'self'
  const parts = (x) => {
    const out = [norm(x.payerId), norm(x.provider || x.insurerName || x.payer_name), normMemberId(x.memberId || x.member_id), rel(x)]
    if (rel(x) !== 'self') out.push(norm(x.subscriberFirstName), norm(x.subscriberLastName), norm(x.subscriberDOB || x.subscriberDob))
    return out.join('|')
  }
  return !!normMemberId(a.memberId || a.member_id) && parts(a) === parts(b)
}

/**
 * Index of the second plan to offer next to the primary at `primaryIdx`, or -1.
 * excludeMemberIds: member ids already on file (the applied primary, the saved secondary).
 */
export function secondActiveIndex(cands, primaryIdx, { excludeMemberIds = [] } = {}) {
  const list = Array.isArray(cands) ? cands : []
  const primary = list[primaryIdx]
  const exclude = new Set(
    [...excludeMemberIds, primary && primary.memberIdGuess].map(normMemberId).filter(Boolean),
  )
  return list.findIndex((c, i) => {
    if (i === primaryIdx || !c || c.active !== true) return false
    const m = normMemberId(c.memberIdGuess)
    return !!m && !exclude.has(m)
  })
}

// True when a fresh discovery result is a material change from the coverage on file:
//   - the applied plan now reads inactive, OR
//   - the applied plan is still there and a SECOND active plan appeared (INSURE-2a), OR
//   - the applied member ID is gone from results and an active plan exists.
// Same plan still active with nothing new (or merely unknown) -> false (stay silent).
export function discoveryChangedFromApplied(fresh, ins) {
  const cands = (fresh && Array.isArray(fresh.candidates)) ? fresh.candidates : []
  const appliedMember = normMemberId(ins && ins.memberId)
  if (appliedMember) {
    const idx = cands.findIndex((c) => normMemberId(c && c.memberIdGuess) === appliedMember)
    if (idx >= 0) {
      if (cands[idx].active === false) return true
      const onFile = [ins && ins.memberId, ins && ins.secondary && ins.secondary.memberId]
      return secondActiveIndex(cands, idx, { excludeMemberIds: onFile }) >= 0
    }
  }
  return cands.some((c) => c && c.active === true)
}

// PROD-306: true when an UNACKNOWLEDGED discovery result is a second-plan offer — the applied
// plan is still there and not ended, and a second active plan with a member id that is not
// already on file sits next to it (the INSURE-2a branch of discoveryChangedFromApplied). Home's
// toast never names a second plan, so dismissing the toast must not retire this offer.
export function isSecondPlanOffer(dr, ins) {
  if (!dr || dr.acknowledged === true) return false
  const cands = Array.isArray(dr.candidates) ? dr.candidates : []
  const appliedMember = normMemberId(ins && ins.memberId)
  if (!appliedMember) return false
  const idx = cands.findIndex((c) => normMemberId(c && c.memberIdGuess) === appliedMember)
  if (idx < 0 || cands[idx].active === false) return false
  const onFile = [ins && ins.memberId, ins && ins.secondary && ins.secondary.memberId]
  return secondActiveIndex(cands, idx, { excludeMemberIds: onFile }) >= 0
}

// The secondary body for POST /users/<id>/secondary-insurance, or null when the candidate
// has no member ID: the server treats an empty memberId as "clear the secondary" (D5).
export function buildDiscoveredSecondary(cand) {
  const c = cand || {}
  const memberId = String(c.memberIdGuess || '').trim()
  if (!normMemberId(memberId)) return null
  const payerId = String(c.payerId || '').trim()
  return {
    provider: String(c.payerName || '').trim(),
    ...(payerId ? { payerId } : {}),
    memberId,
  }
}

// { ok, secondary, eligibility }: ok only when the server echoed the secondary it stored and
// that echo carries the member id that was sent. Anything else is not a save.
export function secondaryFromResponse(result, sent) {
  const echo = result && typeof result === 'object' ? result.secondary : null
  const want = normMemberId(sent && sent.memberId)
  const ok = !!(echo && typeof echo === 'object' && want && normMemberId(echo.memberId) === want)
  return { ok, secondary: ok ? echo : null, eligibility: ok ? (result.eligibility || null) : null }
}

// ★ PROD-424 (g), patient push 3a: WHICH discovery result a toast snooze is for. The stamp
//   (recheckedAt, else ranAt) when the result has one; else a key made of the result itself
//   (status + each candidate's payer, member id and active flag). It used to fall back to the
//   constant 'unstamped', so one snoozed unstamped result hid EVERY later unstamped result for
//   the rest of the session. The store writes this key and Home compares against it.
export function discoverySnoozeKey(dr) {
  if (!dr) return null
  const stamp = dr.recheckedAt || dr.ranAt
  if (stamp) return String(stamp)
  const cands = (Array.isArray(dr.candidates) ? dr.candidates : []).map((c) => [
    (c && c.payerId) || '', normMemberId((c && (c.memberIdGuess || c.memberId)) || ''), c && c.active === true ? 'a' : c && c.active === false ? 'e' : '?',
  ].join(':'))
  return `unstamped|${dr.status || ''}|${cands.join(',')}`
}
